import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListOrganizationMembersQueryKey,
  useInviteOrganizationMember,
  type OrganizationMemberDto,
} from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { Button } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { InviteLinkShare } from '../../components/InviteLinkShare';
import { InviteMemberForm, type InviteMemberFormValues } from '../../components/InviteMemberForm';

interface InviteMemberDialogProps extends DialogBaseProps {
  orgId: string;
  orgSlug: string;
}

function InviteMemberDialog({ orgId, orgSlug, onClose }: InviteMemberDialogProps) {
  const queryClient = useQueryClient();
  const [invited, setInvited] = useState<OrganizationMemberDto | null>(null);

  const { mutate, isPending, error } = useInviteOrganizationMember({
    mutation: {
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationMembersQueryKey({ orgId }),
        });
        setInvited(data);
        toast.success(
          data.status === 'PENDING' ? `Invited ${data.email}` : `Added ${data.email} to the team`
        );
      },
    },
  });

  function handleSubmit(values: InviteMemberFormValues) {
    mutate({ data: { email: values.email, role: values.role }, params: { orgId } });
  }

  if (invited?.status === 'PENDING') {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Invite created
          </DialogTitle>
          <DialogDescription>
            {invited.email} doesn&rsquo;t have an account yet — send them the sign-in link below.
          </DialogDescription>
        </DialogHeader>
        <InviteLinkShare
          link={`${window.location.origin}/orgs/${orgSlug}`}
          email={invited.email}
          onDone={onClose}
        />
      </DialogContent>
    );
  }

  if (invited) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Added to the team
          </DialogTitle>
          <DialogDescription>
            {invited.email} already had an account, so they&rsquo;re in — no invite needed. They can
            switch to this organization from the sidebar next time they&rsquo;re signed in.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end">
          <Button variant="tinted" shape="pill" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          Invite a teammate
        </DialogTitle>
        <DialogDescription>
          They&rsquo;ll get access as soon as they sign in with this email — no invite email is
          sent.
        </DialogDescription>
      </DialogHeader>
      <InviteMemberForm
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={error ? (getApiErrorMessage(error) ?? 'Failed to create the invite') : null}
      />
    </DialogContent>
  );
}

export { InviteMemberDialog };
